import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProjects, useAuth, useTemplateCreation, useInstantMock } from '../hooks';
import { TemplateSelector } from '../components/onboarding';
import { ProjectList } from '../components/project';
import { Hero, Features, HowItWorks, CallToAction } from '../components/landing';
import type { Project, ProjectTemplate } from '@mockd/shared';

export function Home() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { fetchAnonymousProjects } = useProjects();
  const { createFromTemplate, isCreating: isTemplateCreating, loadingTemplateId } = useTemplateCreation();
  const { createInstantMock: handleQuickStart, isCreating } = useInstantMock();
  const [anonymousProjects, setAnonymousProjects] = useState<Project[]>([]);

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Load anonymous projects from localStorage for "Your recent mocks" section
  useEffect(() => {
    const load = async () => {
      const anon = await fetchAnonymousProjects();
      setAnonymousProjects(anon);
    };
    load();
  }, [fetchAnonymousProjects]);

  const handleTemplateSelect = useCallback(async (template: ProjectTemplate) => {
    try {
      const project = await createFromTemplate(template);
      navigate(`/projects/${project.id}?from=template`);
    } catch {
      // Error handled by hook
    }
  }, [createFromTemplate, navigate]);

  // Auto-trigger quickstart when arriving from pricing page CTA
  const quickstartTriggered = useRef(false);
  useEffect(() => {
    if (!authLoading && !user && searchParams.get('quickstart') === 'true' && !quickstartTriggered.current) {
      quickstartTriggered.current = true;
      setSearchParams({}, { replace: true });
      handleQuickStart();
    }
  }, [authLoading, user, searchParams, setSearchParams, handleQuickStart]);

  return (
    <div className="bg-base-200">
      <Hero onQuickStart={handleQuickStart} isLoading={isCreating} />

      {/* Template quick-start strip */}
      <section className="py-12 bg-base-100 border-t border-base-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-1">Start from a template</h2>
          <p className="text-sm text-base-content/70 mb-6">
            Pre-configured projects to get you going in one click.
          </p>
          <TemplateSelector
            size="standard"
            onSelect={handleTemplateSelect}
            isLoading={isTemplateCreating}
            loadingTemplateId={loadingTemplateId}
          />
        </div>
      </section>

      {anonymousProjects.length > 0 && (
        <section className="py-12 bg-base-200 border-t border-base-300">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="mb-1">Your recent mocks</h2>
            <p className="text-sm text-base-content/70 mb-6">
              Pick up where you left off.
            </p>
            <ProjectList
              projects={anonymousProjects}
              endpointCounts={{}}
              layout="grid"
            />
          </div>
        </section>
      )}

      <HowItWorks />
      <Features />
      <CallToAction onQuickStart={handleQuickStart} isLoading={isCreating} />
    </div>
  );
}
